import { useEffect, useRef, useState, useCallback } from "react";

const BUFFER_PAGES = 2; // Render 2 pages above and below visible area

export function useVisiblePages(
  containerRef: React.RefObject<HTMLDivElement | null>,
  pageRefs: React.MutableRefObject<(HTMLDivElement | null)[]>,
  numPages: number
) {
  const [visiblePages, setVisiblePages] = useState<Set<number>>(new Set([1, 2, 3]));
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Calculate which pages should be rendered (visible + buffer)
  const calculateRenderablePages = useCallback((visibleSet: Set<number>): Set<number> => {
    const renderable = new Set<number>();
    
    visibleSet.forEach((page) => {
      // Add the visible page and buffer pages around it
      for (let i = page - BUFFER_PAGES; i <= page + BUFFER_PAGES; i++) {
        if (i >= 1 && i <= numPages) {
          renderable.add(i);
        }
      }
    });

    return renderable;
  }, [numPages]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || numPages === 0) return;

    // Track currently visible pages
    const currentlyVisible = new Set<number>();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const pageNumber = Number(entry.target.getAttribute("data-page-number"));
          if (isNaN(pageNumber)) return;

          if (entry.isIntersecting) {
            currentlyVisible.add(pageNumber);
          } else {
            currentlyVisible.delete(pageNumber);
          }
        });

        // Calculate renderable pages with buffer
        const renderable = calculateRenderablePages(currentlyVisible);
        
        setVisiblePages((prev) => {
          // Only update if the set actually changed
          if (prev.size !== renderable.size || 
              ![...renderable].every((p) => prev.has(p))) {
            return renderable;
          }
          return prev;
        });
      },
      {
        root: container,
        // Use a large margin to start loading pages before they're visible
        rootMargin: "200px 0px 200px 0px",
        threshold: 0,
      }
    );

    // Observe all page containers
    pageRefs.current.forEach((pageEl) => {
      if (pageEl) {
        observerRef.current?.observe(pageEl);
      }
    });

    return () => {
      observerRef.current?.disconnect();
    };
  }, [containerRef, numPages, calculateRenderablePages]);

  // Re-observe when pageRefs change (e.g., after initial render)
  const observePage = useCallback((pageEl: HTMLDivElement | null) => {
    if (pageEl && observerRef.current) {
      observerRef.current.observe(pageEl);
    }
  }, []);

  return { visiblePages, observePage };
}
